<template>
  <div class="home qm-page-bg">
    <div class="page-header">
      <div class="header-top">
        <div class="page-title">选择门店</div>
        <div class="coupon-chip" v-if="memberCoupon">
          {{ memberCoupon.couponName || memberCoupon.typeNo }}
        </div>
      </div>
      <div class="header-controls">
        <div class="city-select qm-glass-card" @click="showCitySelect = true">
          <van-icon name="location" class="city-icon" />
          <span class="city-name">{{ currentCity }}</span>
          <van-icon name="arrow-down" class="arrow-icon" />
        </div>
        <div class="search-box">
          <van-search
            v-model="searchValue"
            placeholder="搜索门店"
            shape="round"
            @input="onSearch"
          />
        </div>
      </div>
    </div>

    <div class="shop-list">
      <van-list v-model="loading" :finished="true" finished-text="— 已加载全部门店 —">
        <div
          class="shop-item"
          v-for="shop in shopList"
          :key="shop.storeCode || shop.id"
          @click="goGoods(shop)"
        >
          <div class="shop-card qm-glass-card">
            <div class="shop-left">
              <div class="shop-avatar">
                <van-icon name="shop-o" />
              </div>
            </div>
            <div class="shop-right">
              <div class="shop-header">
                <span class="name">{{ shop.name }}</span>
                <span class="distance">{{ formatDistance(shop.distance) }}</span>
              </div>
              <div class="shop-tags">
                <span class="tag hall">自取</span>
              </div>
              <p class="address">{{ shop.address }}</p>
              <p class="business-time" v-if="shop.startTime">
                <van-icon name="clock-o" />
                {{ shop.startTime }} - {{ shop.endTime }}
              </p>
            </div>
            <div class="shop-arrow">
              <van-icon name="arrow" />
            </div>
          </div>
        </div>
      </van-list>
    </div>

    <van-popup
      v-model="showCitySelect"
      position="right"
      :style="{ height: '100%', width: '100%' }"
    >
      <div class="city-select-page qm-page-bg">
        <van-nav-bar
          title="选择城市"
          left-arrow
          class="qm-nav-bar"
          @click-left="showCitySelect = false"
        />
        <van-index-bar :index-list="indexList" class="city-list-component">
          <div v-for="(cities, letter) in groupedCities" :key="letter">
            <van-index-anchor :index="letter" />
            <van-cell
              v-for="city in cities"
              :key="city.code"
              :title="city.city"
              @click="selectCity(city)"
            />
          </div>
        </van-index-bar>
      </div>
    </van-popup>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { pinyin } from "pinyin";
import request from "@/utils/request";
import { formatDistance } from "@/utils/tims";

const FALLBACK_CITY = {
  code: "440100",
  city: "广州市",
  lng: 113.32262,
  lat: 23.13216,
  initial: "G",
};

function cityInitial(name) {
  const ch = String(name || "").trim().charAt(0);
  if (!ch) return "#";
  try {
    const arr = pinyin(ch, { style: "first_letter" });
    const letter = String((arr && arr[0] && arr[0][0]) || "")
      .toUpperCase()
      .charAt(0);
    return /[A-Z]/.test(letter) ? letter : "#";
  } catch (e) {
    return "#";
  }
}

export default {
  name: "HomePage",
  data() {
    return {
      currentCity: FALLBACK_CITY.city,
      currentCityInfo: { ...FALLBACK_CITY },
      cityList: [],
      searchValue: "",
      searchTimer: null,
      showCitySelect: false,
      indexList: [],
      groupedCities: {},
      shopList: [],
      loading: false,
      userLocation: null,
    };
  },
  computed: {
    ...mapState(["uuid", "memberCoupon", "appConfig"]),
  },
  async created() {
    if (!this.uuid) {
      this.$router.replace("/cardkey");
      return;
    }
    await this.loadTimsCities();
    await this.getIpLocation();
    await this.searchShops();
  },
  methods: {
    formatDistance,
    applyCityList(cities) {
      const list = (cities || []).map((c) => ({
        code: String(c.code),
        city: String(c.city),
        lng: Number(c.lng) || 0,
        lat: Number(c.lat) || 0,
        initial: cityInitial(c.city),
      }));
      this.cityList = list;
      const indexList = [
        ...new Set(list.map((c) => c.initial).filter((x) => x && x !== "#")),
      ].sort();
      if (list.some((c) => c.initial === "#")) indexList.push("#");
      this.indexList = indexList;
      const grouped = {};
      indexList.forEach((letter) => {
        grouped[letter] = list.filter((c) => c.initial === letter);
      });
      this.groupedCities = grouped;
      // 默认城市对齐天好开城列表
      const gz = this.findCity(FALLBACK_CITY.city) || list[0] || FALLBACK_CITY;
      this.currentCity = gz.city;
      this.currentCityInfo = { ...gz };
    },
    async loadTimsCities() {
      try {
        const resp = await request.post("/tims/cities", {});
        const cities = (resp.data && resp.data.cities) || [];
        if (!cities.length) throw new Error("城市列表为空");
        this.applyCityList(cities);
      } catch (e) {
        console.error("加载天好城市失败:", e);
        this.$toast.fail(e.message || "城市列表加载失败");
        this.applyCityList([FALLBACK_CITY]);
      }
    },
    findCity(cityName) {
      if (!cityName) return null;
      const normalized = String(cityName).endsWith("市")
        ? String(cityName)
        : `${cityName}市`;
      return (
        this.cityList.find(
          (c) => c.city === normalized || c.city === cityName || c.code === cityName
        ) || null
      );
    },
    getCityInfo(cityName) {
      return this.findCity(cityName) || { ...FALLBACK_CITY };
    },
    selectCity(city) {
      const info =
        typeof city === "string" ? this.getCityInfo(city) : { ...city };
      this.currentCity = info.city;
      this.currentCityInfo = info;
      // 手动选城后用该城中心坐标，不再沿用 IP 定位
      this.userLocation = null;
      this.showCitySelect = false;
      this.searchShops();
    },
    onSearch() {
      if (this.searchTimer) clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.searchShops(), 300);
    },
    async getIpLocation() {
      try {
        const resp = await request.get("/mx/ip-location");
        const data = (resp && resp.data) || resp || {};
        if (data.city) {
          const name = String(data.city).endsWith("市")
            ? data.city
            : `${data.city}市`;
          const matched = this.findCity(name);
          if (matched) {
            this.currentCity = matched.city;
            this.currentCityInfo = { ...matched };
          }
          // 不在天好开城列表里则保持默认广州
        }
        if (data.lat && data.lng) {
          this.userLocation = {
            latitude: Number(data.lat),
            longitude: Number(data.lng),
          };
        }
      } catch (e) {
        console.warn("IP定位失败，使用默认广州", e);
      }
    },
    async searchShops() {
      if (!this.uuid) return;
      this.loading = true;
      try {
        const lat = this.userLocation
          ? this.userLocation.latitude
          : this.currentCityInfo.lat;
        const lng = this.userLocation
          ? this.userLocation.longitude
          : this.currentCityInfo.lng;
        const resp = await request.post("/tims/stores", {
          uuid: this.uuid,
          lat,
          lng,
          cityCode: this.currentCityInfo.code,
          name: this.searchValue.trim(),
          pageNo: 1,
          numPerPage: 40,
        });
        this.shopList = (resp.data && resp.data.shops) || [];
        if (!this.shopList.length) {
          this.$toast("附近暂无门店");
        }
      } catch (error) {
        console.error(error);
        this.$toast.fail(error.message || "门店加载失败");
      } finally {
        this.loading = false;
      }
    },
    goGoods(shop) {
      this.$router.push({
        path: "/goods-list",
        query: {
          storeCode: shop.storeCode,
          shopName: shop.name,
          shopAddress: shop.address,
          lat: shop.lat,
          lng: shop.lng,
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.home {
  min-height: 100vh;
  padding-bottom: 24px;
}

.page-header {
  padding: 16px 16px 8px;
  position: sticky;
  top: 0;
  z-index: 10;
  background: rgba(248, 248, 248, 0.92);
  backdrop-filter: blur(8px);

  .header-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
  }

  .page-title {
    font-size: 20px;
    font-weight: 700;
  }

  .coupon-chip {
    max-width: 55%;
    font-size: 11px;
    color: #c8102e;
    background: rgba(200, 16, 46, 0.08);
    padding: 4px 8px;
    border-radius: 999px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .header-controls {
    display: flex;
    gap: 8px;
    align-items: center;
  }

  .city-select {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 8px 10px;
    flex-shrink: 0;
    font-size: 13px;
  }

  .search-box {
    flex: 1;
    ::v-deep .van-search {
      padding: 0;
      background: transparent;
    }
  }
}

.shop-list {
  padding: 8px 16px;
}

.shop-card {
  display: flex;
  align-items: center;
  padding: 14px;
  margin-bottom: 10px;
}

.shop-avatar {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: #f3f3f3;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  color: #c8102e;
  font-size: 22px;
}

.shop-right {
  flex: 1;
  min-width: 0;

  .shop-header {
    display: flex;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 4px;
  }

  .name {
    font-weight: 600;
    font-size: 15px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .distance {
    color: #999;
    font-size: 12px;
    flex-shrink: 0;
  }

  .shop-tags {
    margin-bottom: 4px;
    .tag {
      font-size: 10px;
      padding: 1px 6px;
      border-radius: 4px;
      background: #fff1f0;
      color: #c8102e;
    }
  }

  .address,
  .business-time {
    font-size: 12px;
    color: #888;
    margin-top: 2px;
  }
}

.shop-arrow {
  color: #ccc;
  margin-left: 6px;
}

.city-select-page {
  min-height: 100%;
}
</style>
